import { useMemo } from "react";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";
import type { ActionCandidate } from "../../../../../types/candidate";

/**
 * The actions an editor for `action` may reference as data sources: the
 * actions on its own execution path BEFORE it -- actions that ran before the
 * enclosing Conditional Logic block plus earlier actions within the same
 * branch/loop path. Sibling branches (and anything nested inside them) are
 * excluded, so their outputs never leak into the picker (spec 8.1/8.2). When
 * there is no current action (trigger / branch-condition panels), falls back
 * to the full flat list unchanged.
 *
 * Memoized on a content fingerprint because upstreamActionsOfAction is a store
 * METHOD (not a computed) and returns a fresh array identity on every call --
 * without this, child `useEffect([actions])` hooks would re-run every render
 * (mirrors the SelectFindRecords fingerprint-memo).
 */
export const useUpstreamActions = (
  candidate: CandidateStore,
  action?: ActionCandidate,
): ActionCandidate[] => {
  const base =
    action !== undefined
      ? candidate.branches.upstreamActionsOfAction(action.hash)
      : candidate.allActions;

  const fingerprint = base
    .map(
      (a) =>
        `${a.hash}:${a.actionReference?.instanceId ?? ""}:${a.actionName ?? ""}`,
    )
    .join("|");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fingerprint is the content key for `base`
  return useMemo(() => base, [fingerprint]);
};
